import { useMemo } from 'react';
import { getStroke } from 'perfect-freehand';
import type { StrokeItem } from '../../doc/schema';

/** Freehand stroke: an outline polygon from perfect-freehand, scaled with the item box. */
export function StrokeBody({ item }: { item: StrokeItem }) {
  const { points, w0, h0, color, tool, size, pressure } = item.props;
  const d = useMemo(() => strokePath(points, size, tool, pressure), [points, size, tool, pressure]);
  return (
    <svg
      className={`stroke-svg stroke-${tool}`}
      viewBox={`0 0 ${Math.max(1, w0)} ${Math.max(1, h0)}`}
      preserveAspectRatio="none"
      overflow="visible"
      aria-hidden
    >
      <path d={d} fill={color} />
    </svg>
  );
}

export function strokeOptions(size: number, tool: 'pen' | 'highlighter', realPressure: boolean) {
  return {
    size,
    thinning: tool === 'pen' ? 0.55 : 0,
    smoothing: 0.5,
    streamline: 0.45,
    simulatePressure: tool === 'pen' && !realPressure,
    last: true,
  };
}

/** Flat [x, y, p, ...] to an SVG path of the stroke outline. */
export function strokePath(flat: number[], size: number, tool: 'pen' | 'highlighter', realPressure: boolean): string {
  const pts: [number, number, number][] = [];
  for (let i = 0; i + 2 < flat.length; i += 3) pts.push([flat[i]!, flat[i + 1]!, flat[i + 2]!]);
  if (!pts.length) return '';
  return outlineToPath(getStroke(pts, strokeOptions(size, tool, realPressure)));
}

export function outlineToPath(outline: number[][]): string {
  if (outline.length < 2) return '';
  let d = '';
  const n = outline.length;
  for (let i = 0; i < n; i++) {
    const a = outline[i]!;
    const b = outline[(i + 1) % n]!;
    const mx = (a[0]! + b[0]!) / 2;
    const my = (a[1]! + b[1]!) / 2;
    d += i === 0 ? `M${a[0]!.toFixed(2)},${a[1]!.toFixed(2)}` : '';
    d += `Q${a[0]!.toFixed(2)},${a[1]!.toFixed(2)} ${mx.toFixed(2)},${my.toFixed(2)}`;
  }
  return d + 'Z';
}
